import { FeatureSwitchInfo } from '@if/core/types'
import { createContext, useContext } from 'react'
import { FinancingConfigDto } from '../data/types/FinancingConfigDto'
import { MechanicalWarrantyProvider } from '../data/types/MechanicalWarrantyProvider'
import { ProductsProvider } from '../data/types/ProductsProvider'
import { ProvinceConfigs } from '../data/types/ProvinceConfigs'
import { BusinessError, Territory } from '../data/types'
import { DocumentType } from '../data/types/DocumentType'
import { TaskType } from '../data/types/TaskType'
import { TranslatedEnum } from '../data/types/TranslatedEnum'
import { ValueListDto } from '../data/api/config/config-api'
import { Conflict } from '../data/types/Conflict'

export interface AppConfigContextValue {
  // Error state
  businessErrors: BusinessError[]
  globalTechnicalError: string | null
  setBusinessErrors: (errors: BusinessError[]) => void
  setTechnicalError: (error: string | null) => void
  resetErrors: () => void

  // Conflict state
  conflict: Conflict | null
  setConflict: (conflict: Conflict | null) => void

  // Loading state
  isInitializing: boolean
  isDeprecated: boolean
  setDeprecated: (deprecated: boolean) => void

  // Language
  language: string
  setLanguage: (lang: string) => void

  // Value lists (raw DTOs for backward compatibility with selectors)
  creditRefusalReasons: ValueListDto | null
  merchantTags: ValueListDto | null

  // Config data
  territories: Territory[]
  financingConfigs: FinancingConfigDto[]
  provinceConfigurations: ProvinceConfigs
  productsProviders: ProductsProvider[]
  mechanicalWarrantyProvider: MechanicalWarrantyProvider
  iFinanceHolidays: Date[]

  // Enums
  jobTypeEnum: Record<string, TranslatedEnum>
  otherIncomeTypesEnum: Record<string, TranslatedEnum>
  applicantTypeEnum: Record<string, TranslatedEnum>
  financingProgramEnum: Record<string, TranslatedEnum>
  autoPlanEnums: Record<string, TranslatedEnum>
  personalLoanPlanEnums: Record<string, TranslatedEnum>
  ifxPlanEnums: Record<string, TranslatedEnum>
  b2cPlanEnums: Record<string, TranslatedEnum>
  creditRslaPlanEnums: Record<string, TranslatedEnum>
  vlvrPlanEnums: Record<string, TranslatedEnum>

  // Document and Task types
  documentTypes: Record<number, DocumentType>
  taskTypes: Record<number, TaskType>

  // Feature flags
  featureSwitchInfo: FeatureSwitchInfo | null
}

export const AppConfigContext = createContext<AppConfigContextValue | null>(null)

export function useAppConfigContext(): AppConfigContextValue {
  const context = useContext(AppConfigContext)
  if (!context) {
    throw new Error('useAppConfigContext must be used within an AppConfigProvider')
  }
  return context
}
